import { i as LEAVE_ALT_SCREEN, n as injectOutputStore, r as FullScreen, s as KiddProvider } from "./store-key-fUtc4MNq.js";
import { isFunction } from "@kidd-cli/utils/fp";
import { match as match$1 } from "ts-pattern";
import { withTag } from "@kidd-cli/utils/tag";
import "react";
import { jsx } from "react/jsx-runtime";
import process from "node:process";
//#region src/screen/output/screen-log.ts
/**
* Create a {@link Log} instance that writes to an {@link OutputStore}
* for rendering by the `<Output />` component.
*
* @param store - The output store to push entries to.
* @returns A frozen Log instance compatible with `ctx.log`.
*/
function createScreenLog(store) {
	return Object.freeze({
		info(message, _opts) {
			store.push({
				kind: "log",
				level: "info",
				text: message
			});
		},
		success(message, _opts) {
			store.push({
				kind: "log",
				level: "success",
				text: message
			});
		},
		error(message, _opts) {
			store.push({
				kind: "log",
				level: "error",
				text: message
			});
		},
		warn(message, _opts) {
			store.push({
				kind: "log",
				level: "warn",
				text: message
			});
		},
		step(message, _opts) {
			store.push({
				kind: "log",
				level: "step",
				text: message
			});
		},
		message(message, opts) {
			const symbol = match$1(opts).with(void 0, () => void 0).otherwise((o) => o.symbol);
			store.push({
				kind: "log",
				level: "message",
				text: message,
				symbol
			});
		},
		intro(_title) {},
		outro(_message) {},
		note(_message, _title, _opts) {},
		newline() {
			store.push({ kind: "newline" });
		},
		raw(text) {
			store.push({
				kind: "raw",
				text
			});
		},
		box(_message, _title) {},
		stream: createScreenStreamLog()
	});
}
/**
* Create a no-op {@link StreamLog} for screen contexts.
*
* @private
*/
function createScreenStreamLog() {
	const noop = async (_iterable) => {};
	return Object.freeze({
		info: noop,
		success: noop,
		error: noop,
		warn: noop,
		step: noop,
		message: noop
	});
}
//#endregion
//#region src/screen/output/screen-report.ts
/**
* Create a {@link Report} instance that writes to an {@link OutputStore}
* for rendering by the `<Output />` component.
*
* @param store - The output store to push entries to.
* @returns A frozen Report instance compatible with `ctx.report`.
*/
function createScreenReport(store) {
	return Object.freeze({
		check(input) {
			store.push({
				kind: "check",
				input
			});
		},
		finding(input) {
			store.push({
				kind: "finding",
				input
			});
		},
		summary(input) {
			store.push({
				kind: "summary",
				input
			});
		}
	});
}
//#endregion
//#region src/screen/output/screen-spinner.ts
/**
* Create a {@link Spinner} instance that manages state in an {@link OutputStore}
* for rendering by the `<Output />` component.
*
* @param store - The output store to update spinner state on.
* @returns A frozen Spinner instance compatible with `ctx.status.spinner`.
*/
function createScreenSpinner(store) {
	return Object.freeze({
		start(message) {
			store.setSpinner({
				status: "spinning",
				message: message ?? "Loading..."
			});
		},
		stop(message) {
			store.setSpinner({
				status: "stopped",
				message: message ?? ""
			});
		},
		message(text) {
			store.setSpinner({
				status: "spinning",
				message: text ?? ""
			});
		},
		cancel(message) {
			store.setSpinner({
				status: "cancelled",
				message: message ?? ""
			});
		},
		error(message) {
			store.setSpinner({
				status: "error",
				message: message ?? ""
			});
		},
		clear() {
			store.setSpinner({ status: "idle" });
		},
		get isCancelled() {
			return store.getSnapshot().spinner.status === "cancelled";
		}
	});
}
//#endregion
//#region src/screen/output/store.ts
/**
* Create an external output store for accumulating log, report,
* and spinner state in screen components.
*
* @returns A frozen {@link OutputStore} instance.
*/
function createOutputStore() {
	const entries = [];
	const subscribers = /* @__PURE__ */ new Set();
	let nextId = 0;
	const idle = Object.freeze({ status: "idle" });
	let spinnerState = idle;
	let snapshot = Object.freeze({
		entries: Object.freeze([]),
		spinner: idle
	});
	/**
	* Rebuild the snapshot and notify all subscribers.
	*
	* @private
	*/
	const notify = () => {
		snapshot = Object.freeze({
			entries: Object.freeze([...entries]),
			spinner: spinnerState
		});
		[...subscribers].map((cb) => cb());
	};
	return Object.freeze({
		getSnapshot() {
			return snapshot;
		},
		subscribe(callback) {
			subscribers.add(callback);
			return () => {
				subscribers.delete(callback);
			};
		},
		push(entry) {
			const id = nextId;
			nextId += 1;
			entries.push({
				...entry,
				id
			});
			notify();
		},
		setSpinner(state) {
			spinnerState = state;
			notify();
		}
	});
}
//#endregion
//#region src/screen/screen.tsx
/**
* Define a screen command that renders a React/Ink TUI.
*
* The `render` property accepts a React component that receives the
* parsed args as props. The full command context — including `log`,
* `status`, and any middleware-decorated properties like `report` —
* is available via `useScreenContext()`.
*
* Imperative I/O properties (`log`, `status`, `report`) are automatically
* swapped with React-backed implementations that render through the
* `<Output />` component, so the same interface works in both
* `command()` and `screen()` contexts.
*
* @param def - Screen definition including description, options, exit behavior, and render component.
* @returns A tagged Command object compatible with the kidd autoloader and command map.
*/
function screen(def) {
	const exitMode = def.exit ?? "manual";
	const ScreenComponent = def.render;
	const renderFn = async (ctx) => {
		const { render: inkRender } = await import("ink");
		const screenCtx = toScreenContext(ctx);
		const isFullscreen = await match$1(isFunction(def.fullscreen)).with(true, () => def.fullscreen(screenCtx)).with(false, () => def.fullscreen === true).exhaustive();
		const instance = inkRender(/* @__PURE__ */ jsx(KiddProvider, {
			value: screenCtx,
			children: match$1(isFullscreen).with(true, () => /* @__PURE__ */ jsx(FullScreen, { children: /* @__PURE__ */ jsx(ScreenComponent, { ...ctx.args }) })).with(false, () => /* @__PURE__ */ jsx(ScreenComponent, { ...ctx.args })).exhaustive()
		}));
		if (exitMode === "auto") {
			const { unmount } = instance;
			setTimeout(() => {
				unmount();
			}, 0);
		}
		try {
			await instance.waitUntilExit();
		} finally {
			if (isFullscreen) process.stdout.write(LEAVE_ALT_SCREEN);
		}
	};
	return withTag({
		aliases: def.aliases,
		deprecated: resolveValue(def.deprecated),
		description: resolveValue(def.description),
		hidden: resolveValue(def.hidden),
		name: def.name,
		options: def.options,
		positionals: def.positionals,
		render: renderFn,
		strict: def.strict
	}, "Command");
}
/**
* Keys stripped from the screen context (no screen-safe equivalent).
*
* @private
*/
const STRIPPED_KEYS = new Set([
	"colors",
	"fail",
	"format",
	"prompts"
]);
/**
* Convert a full {@link CommandContext} into a {@link ScreenContext} by
* replacing imperative I/O properties with React-backed implementations.
*
* Creates an {@link OutputStore} and swaps `log`, `status.spinner`, and any
* middleware-decorated `report` with screen-backed versions that push
* entries to the store. The store is attached via {@link OUTPUT_STORE_KEY}
* (a private symbol) so `<Output />` can subscribe to it.
*
* @param ctx - The full command context.
* @returns A ScreenContext with React-backed I/O.
*/
function toScreenContext(ctx) {
	const store = createOutputStore();
	const screenLog = createScreenLog(store);
	const screenSpinner = createScreenSpinner(store);
	const ctxRecord = ctx;
	const baseEntries = Object.keys(ctx).filter((key) => !STRIPPED_KEYS.has(key)).map((key) => [key, ctxRecord[key]]);
	const reportEntries = match$1("report" in ctx).with(true, () => [["report", createScreenReport(store)]]).with(false, () => []).exhaustive();
	const screenCtx = injectOutputStore({
		ctx: Object.fromEntries([
			...baseEntries,
			["log", screenLog],
			["status", {
				...ctx.status,
				spinner: screenSpinner
			}],
			...reportEntries
		]),
		store
	});
	return Object.freeze(screenCtx);
}
/**
* Resolve a {@link Resolvable} value.
*
* @private
*/
function resolveValue(value) {
	return match$1(isFunction(value)).with(true, () => value()).with(false, () => value).exhaustive();
}
//#endregion
//#region src/screen/render.tsx
/**
* Render a React element as a live Ink terminal application with the kidd
* screen context injected.
*
* Wraps the provided node in a {@link KiddProvider} that supplies the
* screen context (React-backed `log`, `spinner`, `store`, and any
* middleware-decorated properties). Returns the raw Ink {@link Instance}
* so callers have full lifecycle control — `waitUntilExit()`, `unmount()`,
* `rerender()`, etc.
*
* @param node - The React element to render.
* @param ctx - The command context to convert and inject as screen context.
* @param options - Optional Ink render options (stdout, stdin, debug, etc.).
* @returns The Ink render instance.
*/
async function render(node, ctx, options) {
	const { render: inkRender } = await import("ink");
	return inkRender(/* @__PURE__ */ jsx(KiddProvider, {
		value: toScreenContext(ctx),
		children: node
	}), options);
}
/**
* Render a React element to a string with the kidd screen context injected.
*
* Wraps the provided node in a {@link KiddProvider} and delegates to Ink's
* `renderToString`. Useful for generating documentation, writing output to
* files, testing, or scenarios where rendered output is needed as a string
* without a persistent terminal session.
*
* @param node - The React element to render.
* @param ctx - The command context to convert and inject as screen context.
* @param options - Optional render-to-string options (columns width).
* @returns The rendered string output.
*/
async function renderToString(node, ctx, options) {
	const { renderToString: inkRenderToString } = await import("ink");
	return inkRenderToString(/* @__PURE__ */ jsx(KiddProvider, {
		value: toScreenContext(ctx),
		children: node
	}), options);
}
//#endregion
export { renderToString as n, screen as r, render as t };

//# sourceMappingURL=render-BsTEEL-N.js.map